import React from 'react';

export interface FunnelStep {
    label: string;
    value: number;
    color?: string;
}

export interface FunnelBlockData {
    title?: string;
    steps: FunnelStep[];
}

interface FunnelBlockProps {
    data: FunnelBlockData;
    /** Callback when a step is clicked — used for drill-down */
    onStepClick?: (step: FunnelStep, index: number) => void;
}

const DEFAULT_COLORS = [
    'linear-gradient(90deg, #3b82f6, #60a5fa)',
    'linear-gradient(90deg, #8b5cf6, #a78bfa)',
    'linear-gradient(90deg, #f59e0b, #fbbf24)',
    'linear-gradient(90deg, #ef4444, #f87171)',
    'linear-gradient(90deg, #22c55e, #4ade80)',
    'linear-gradient(90deg, #06b6d4, #22d3ee)',
    'linear-gradient(90deg, #ec4899, #f472b6)',
];

export const FunnelBlock: React.FC<FunnelBlockProps> = ({ data, onStepClick }) => {
    if (!data.steps || data.steps.length === 0) {
        return <p className="text-sm text-gray-500 py-2">Ingen data for trakt-visning</p>;
    }

    const maxValue = Math.max(...data.steps.map(s => s.value));

    return (
        <div className="mt-2">
            {data.title && (
                <h4 className="text-sm font-semibold text-gray-700 mb-2">{data.title}</h4>
            )}
            <div className="flex flex-col gap-1.5 py-2">
                {data.steps.map((step, idx) => {
                    const pct = maxValue > 0 ? (step.value / maxValue) * 100 : 0;
                    const conversionPct = maxValue > 0
                        ? ((step.value / maxValue) * 100).toFixed(0)
                        : '0';
                    const gradient = step.color || DEFAULT_COLORS[idx % DEFAULT_COLORS.length];

                    return (
                        <div
                            key={idx}
                            className="flex items-center gap-3 w-full"
                        >
                            {/* Count label */}
                            <span className="text-xs text-gray-500 min-w-[70px] text-right tabular-nums">
                                {step.value.toLocaleString('nb-NO')}
                            </span>

                            {/* Funnel bar */}
                            <div
                                className={`h-9 rounded-md flex items-center px-3 text-xs font-semibold text-white transition-all duration-300 ${
                                    onStepClick ? 'cursor-pointer hover:brightness-110' : ''
                                }`}
                                style={{
                                    width: `${Math.max(pct, 8)}%`,
                                    background: gradient,
                                    minWidth: '60px',
                                }}
                                onClick={() => onStepClick?.(step, idx)}
                                title={`${step.label}: ${step.value.toLocaleString('nb-NO')} (${conversionPct}%)`}
                            >
                                {step.label}
                            </div>

                            {/* Percentage */}
                            <span className="text-xs text-gray-400 min-w-[40px]">
                                {conversionPct}%
                            </span>
                        </div>
                    );
                })}
            </div>

            {/* Conversion indicators between steps */}
            {data.steps.length > 1 && (
                <div className="flex flex-wrap gap-3 mt-2 px-[70px]">
                    {data.steps.slice(1).map((step, idx) => {
                        const prevValue = data.steps[idx].value;
                        const dropPct = prevValue > 0
                            ? ((1 - step.value / prevValue) * 100).toFixed(0)
                            : '0';
                        const dropRate = parseFloat(dropPct);

                        return (
                            <span
                                key={idx}
                                className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                                    dropRate > 30
                                        ? 'bg-red-50 text-red-600 border border-red-200'
                                        : dropRate > 15
                                            ? 'bg-amber-50 text-amber-600 border border-amber-200'
                                            : 'bg-green-50 text-green-600 border border-green-200'
                                }`}
                            >
                                {data.steps[idx].label} → {step.label}: -{dropPct}%
                            </span>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
